import React from 'react';
import { Sparkles, Zap, Layers, Palette, Shield, RefreshCw } from 'lucide-react';
import { LayoutTextFlip } from './LayoutTextFlip';
import { StickyScroll } from './StickyScroll';
import '../styles/features.css';

// Import local images
import aiAgentDiscoveryImg from '../assets/ai_agent_discovery.png';
import instantGenerationImg from '../assets/instant_generation.png';
import multiLevelSupportImg from '../assets/multi_level_support.png';
import fullyCustomizableImg from '../assets/fully_customizable.png';
import academicIntegrityImg from '../assets/academic_integrity.png';
import continuousUpdatesImg from '../assets/continuous_updates.png';

import { Highlighter } from './Highlighter';

export default function Features() {
  const scrollContent = [
    {
      title: 'AI Agent Discovery',
      description: (
        <>
          Unlock the full potential of the academic internet with our specialized AI agents. Unlike standard search engines, our agents think like <Highlighter isView={true} color="#67e8f9">expert educators</Highlighter>, traversing verified journals and archives to surface high-quality, peer-reviewed materials. By understanding pedagogical context, they filter out noise and deliver only the most credible sources, ensuring your lectures are grounded in verified knowledge.
        </>
      ),
      content: (
        <div className="h-full w-full flex items-center justify-center text-white overflow-hidden bg-gradient-to-br from-cyan-500 to-blue-600">
          <img src={aiAgentDiscoveryImg} alt="AI Agent Discovery" className="h-full w-full object-cover" />
        </div>
      )
    },
    {
      title: 'Instant Generation',
      description: (
        <>
          Transform hours of manual preparation into <Highlighter isView={true} color="#fca5a5">minutes</Highlighter> of automated brilliance. Our advanced engine synthesizes complex topics into structured, visually engaging presentations, detailed lecture notes, and comprehensive student handouts. It organizes information logically to facilitate learning retention, giving you back the time to focus on what matters most: connecting with your students.
        </>
      ),
      content: (
        <div className="h-full w-full flex items-center justify-center text-white overflow-hidden bg-gradient-to-br from-yellow-400 to-orange-500">
          <img src={instantGenerationImg} alt="Instant Generation" className="h-full w-full object-cover" />
        </div>
      )
    },
    {
      title: 'Multi-Level Support',
      description: (
        <>
          TeachGenie™.ai instantly <Highlighter isView={true} color="#fcd34d">recalibrates</Highlighter> content complexity to suit any audience. Teaching a complex physics concept? Toggle the explanation from a playful, analogy-rich version for elementary students to a rigorous analysis for university graduates with a single click. This flexibility ensures you are always speaking your students’ language, making difficult topics accessible to everyone.
        </>
      ),
      content: (
        <div className="h-full w-full flex items-center justify-center text-white overflow-hidden bg-gradient-to-br from-pink-500 to-rose-600">
          <img src={multiLevelSupportImg} alt="Multi-Level Support" className="h-full w-full object-cover" />
        </div>
      )
    },
    {
      title: 'Fully Customizable',
      description: (
        <>
          We believe AI should be a <Highlighter isView={true} color="#6ee7b7">partner</Highlighter>, not a replacement. After the AI generates your baseline materials, our intuitive editor puts you in the driver’s seat. Rewrite text, swap images, and inject your personal anecdotes with ease. Every element is fully editable, combining the efficiency of automation with the creative freedom to create materials that are authentically yours.
        </>
      ),
      content: (
        <div className="h-full w-full flex items-center justify-center text-white overflow-hidden bg-gradient-to-br from-emerald-400 to-teal-600">
          <img src={fullyCustomizableImg} alt="Fully Customizable" className="h-full w-full object-cover" />
        </div>
      )
    },
    {
      title: 'Academic Integrity',
      description: (
        <>
          TeachGenie™.ai stands as a <Highlighter isView={true} color="#93c5fd">fortress of credibility</Highlighter>. Every fact and claim generated by our AI is rigorously cross-referenced against trusted datasets to minimize hallucinations. We automatically generate accurate citations in your preferred format (APA, MLA, Chicago) and include built-in plagiarism detection, guaranteeing that your course materials are not only original but also ethically sound.
        </>
      ),
      content: (
        <div className="h-full w-full flex items-center justify-center text-white overflow-hidden bg-gradient-to-br from-blue-500 to-indigo-600">
          <img src={academicIntegrityImg} alt="Academic Integrity" className="h-full w-full object-cover" />
        </div>
      )
    },
    {
      title: 'Continuous Updates',
      description: (
        <>
          The field of education is moving fast, and your tools should keep pace. TeachGenie™.ai <Highlighter isView={true} color="#c4b5fd">evolves</Highlighter> alongside the latest advancements in AI and pedagogy, continuously updating with new research and standards. We regularly roll out feature enhancements inspired by teacher feedback, ensuring you have a future-proof teaching assistant that grows and improves every single day.
        </>
      ),
      content: (
        <div className="h-full w-full flex items-center justify-center text-white overflow-hidden bg-gradient-to-br from-violet-500 to-purple-600">
          <img src={continuousUpdatesImg} alt="Continuous Updates" className="h-full w-full object-cover" />
        </div>
      )
    }
  ];

  return (
    <section id="features" className="features">
      <div className="container">
        <div className="section-header reveal-down">
          <h2 className="reveal-text">
            Why Choose <LayoutTextFlip words={["TeachGenie™.ai?", "Instant Speed?", "AI Power?", "Precision?", "Gamification?"]} />
          </h2>
          <p>Everything you need to create exceptional lectures powered by AI</p>
        </div>

        <StickyScroll content={scrollContent} />
      </div>
    </section>
  );
}
